import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	DataCustomDetailTct,
	CustomDetailEnum,
	CustomDetailDataTypeEnum,
} from '@dealstack/shared/lib/generated';
import { ENTITY_TABS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import {
	CREATE_CUSTOM_FIELD_FORM_FIELD,
	CreateCustomFieldDto,
	CreateCustomFieldSchema,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/components/investorData.zod.ts';
import { useCreateCustomDetail } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/api/useCreateCustomDetail.ts';
import { useDeleteCustomDetail } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/api/useDeleteCustomDetail.ts';
import { useUpdateCustomDetail } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/api/useUpdateCustomDetail.ts';

export const useCreateCustomFieldDrawer = (
	onClose: () => void,
	activeTab: ENTITY_TABS,
	editField?: DataCustomDetailTct | null,
	mipId?: string
) => {
	const tMipManage = useTranslationMipManage();
	const isEditMode = !!editField;

	const createCustomDetailMutation = useCreateCustomDetail(
		tMipManage('investor_data.custom_fields.created_success')
	);

	const updateCustomDetailMutation = useUpdateCustomDetail(
		mipId,
		tMipManage('investor_data.custom_fields.updated_success')
	);

	const deleteCustomDetailMutation = useDeleteCustomDetail(
		tMipManage('investor_data.custom_fields.deleted_success')
	);

	const DEFAULT_FORM_VALUES: CreateCustomFieldDto = useMemo(() => {
		if (editField) {
			return {
				[CREATE_CUSTOM_FIELD_FORM_FIELD.name]: editField.name,
				[CREATE_CUSTOM_FIELD_FORM_FIELD.instruction]: editField.instruction || '',
				[CREATE_CUSTOM_FIELD_FORM_FIELD.isChecked]: editField.isChecked,
			};
		}

		return {
			[CREATE_CUSTOM_FIELD_FORM_FIELD.name]: '',
			[CREATE_CUSTOM_FIELD_FORM_FIELD.instruction]: '',
			[CREATE_CUSTOM_FIELD_FORM_FIELD.isChecked]: false,
		};
	}, [editField]);

	const createCustomFieldForm = useForm<CreateCustomFieldDto>({
		mode: 'all',
		resolver: zodResolver(CreateCustomFieldSchema),
		defaultValues: DEFAULT_FORM_VALUES,
		shouldFocusError: false,
	});

	useEffect(() => {
		createCustomFieldForm.reset(DEFAULT_FORM_VALUES);
	}, [editField, DEFAULT_FORM_VALUES, createCustomFieldForm]);

	const onSubmit = () => async (formValues: CreateCustomFieldDto) => {
		const type =
			activeTab === ENTITY_TABS.INDIVIDUAL
				? CustomDetailEnum.INDIVIDUAL
				: CustomDetailEnum.VEHICLE;

		if (isEditMode && editField) {
			updateCustomDetailMutation.mutate(
				{
					customDetailId: editField.id,
					name: formValues.name,
					instruction: formValues.instruction || '',
					isChecked: formValues.isChecked,
					triggerInvestorReevaluate: true,
				},
				{
					onSuccess: () => {
						createCustomFieldForm.reset({ ...DEFAULT_FORM_VALUES });
						onClose();
					},
				}
			);
		} else {
			const payload = {
				name: formValues.name,
				instruction: formValues.instruction || '',
				isChecked: formValues.isChecked,
				type,
				dataType: CustomDetailDataTypeEnum.STRING,
				triggerInvestorReevaluate: true,
			};

			createCustomDetailMutation.mutate(payload, {
				onSuccess: () => {
					createCustomFieldForm.reset({ ...DEFAULT_FORM_VALUES });
					onClose();
				},
			});
		}
	};

	const handleDelete = () => {
		if (!editField) {
			return;
		}

		deleteCustomDetailMutation.mutate(
			{ customDetailId: editField.id },
			{
				onSuccess: () => {
					createCustomFieldForm.reset({ ...DEFAULT_FORM_VALUES });
					onClose();
				},
			}
		);
	};

	return {
		createCustomFieldRequest: isEditMode
			? updateCustomDetailMutation
			: createCustomDetailMutation,
		onSubmit,
		createCustomFieldForm,
		isLoading: createCustomDetailMutation.isPending || updateCustomDetailMutation.isPending,
		handleDelete,
	};
};
